// ============================================================================
// GovernanceAccessTab — Acces & gouvernance (CE / CG / Directeurs, lot 4)
// ============================================================================
// AFFICHAGE uniquement — 3 modeles separes (committee, ExecutiveGrants, Teams).
// Octroi/revocation CE existants reaffichés ; aucune nouvelle capacite d'ecriture CG.

import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Eye, Loader2, Plus, Trash2, UserCheck } from 'lucide-react';
import {
  ConfirmDialog,
  DataTable,
  EmptyState,
  GsSegmented,
  QueryError,
  SkeletonCard,
  StatusBadge,
  GS_COMPONENTS,
  type Column,
  type GsSegmentedOption,
} from '@gs/ui';
import { useTranslation } from '../../i18n';
import {
  useUpsertExecutiveGrant,
  useRevokeExecutiveGrant,
  adminExecutiveGrantErrorCode,
  type AdminExecutiveGrantsResponse,
  type ExecutiveGrantRow,
} from '../../hooks/useAdminExecutiveGrants';
import {
  type AdminMembersResponse,
} from '../../hooks/useAdminMembers';
import {
  buildCeGovernanceRows,
  buildCgGovernanceRows,
  buildDirectorEmailSet,
  buildDirectorGovernanceRows,
  normGovernanceEmail,
  type GovernancePersonRow,
} from '../../lib/admin/governanceRows';
import {
  classifyGovernanceDeviations,
  countGovernanceDeviations,
  deviationI18nKey,
  isCeAccessNominal,
  type GovernanceTab,
} from '../../lib/admin/governanceDeviations';
import { formatDateTimeFr } from '../../lib/format/date';
import { orgLevelI18nKey } from '../../lib/org/deriveOrgLevel';
import { SectionHeader } from './SectionHeader';

type GovernanceSection = 'ce' | 'cg' | 'directors';

function PersonNameCell({ row }: { row: GovernancePersonRow }) {
  const { t } = useTranslation();
  if (row.fullName) {
    return <span className="font-medium text-slate-900 dark:text-slate-100">{row.fullName}</span>;
  }
  return (
    <span className="text-slate-400 dark:text-slate-500" title={row.email}>
      {t('gest:admin.governance.nameMissing')}
    </span>
  );
}

function DeviationSummary({
  rows,
  tab,
}: {
  rows: readonly GovernancePersonRow[];
  tab: GovernanceTab;
}) {
  const { t } = useTranslation();
  const count = countGovernanceDeviations(rows, tab);
  if (count === 0) {
    return (
      <p className="text-meta text-slate-500 dark:text-slate-400" role="status">
        {t('gest:admin.governance.summary.zero')}
      </p>
    );
  }
  return (
    <p
      className="text-meta font-medium text-amber-800 dark:text-amber-200"
      role="status"
    >
      {count === 1
        ? t('gest:admin.governance.summary.countOne', { value: count })
        : t('gest:admin.governance.summary.countOther', { value: count })}
    </p>
  );
}

function EmploymentStatusCell({ row }: { row: GovernancePersonRow }) {
  const { t } = useTranslation();
  if (row.isEmployed === null) {
    return (
      <StatusBadge variant="warning">
        {t('gest:admin.governance.statusProfileMissing')}
      </StatusBadge>
    );
  }
  return (
    <StatusBadge variant={row.isEmployed ? 'success' : 'neutral'}>
      {row.isEmployed
        ? t('gest:admin.governance.statusEmployed')
        : t('gest:admin.governance.statusInactive')}
    </StatusBadge>
  );
}

function CeAccessIndicators({ row }: { row: GovernancePersonRow }) {
  const { t } = useTranslation();
  const accessDeviations = classifyGovernanceDeviations(row, 'ce').filter(
    (kind) => kind !== 'profileMissing',
  );

  if (isCeAccessNominal(row) && accessDeviations.length === 0) {
    return (
      <div className="flex flex-wrap gap-1">
        <StatusBadge variant="neutral">{t('gest:admin.governance.indicators.ceSeat')}</StatusBadge>
        <StatusBadge variant="neutral">
          {t('gest:admin.governance.indicators.ceGrantActive')}
        </StatusBadge>
      </div>
    );
  }

  const badges: ReactNode[] = [];

  if (row.ceGrantActive && !row.ceSeat) {
    badges.push(
      <StatusBadge key="ceReadWithoutSeat" variant="warning">
        {t(deviationI18nKey('ceReadWithoutSeat'))}
      </StatusBadge>,
    );
  }
  if (row.ceSeat && !row.ceGrantActive) {
    badges.push(
      <StatusBadge key="ceSeatWithoutRead" variant="warning">
        {t(deviationI18nKey('ceSeatWithoutRead'))}
      </StatusBadge>,
    );
  }
  if (row.grantIsRevoked && !row.ceGrantActive) {
    badges.push(
      <StatusBadge key="ceGrantRevoked" variant="warning">
        {t('gest:admin.governance.indicators.ceGrantRevoked')}
      </StatusBadge>,
    );
  }

  if (badges.length === 0) {
    return <span className="text-slate-400 dark:text-slate-500">{'—'}</span>;
  }

  return <div className="flex flex-wrap gap-1">{badges}</div>;
}

function ViewProfileAction({ email, name }: { email: string; name: string }) {
  const { t } = useTranslation();
  return (
    <Link
      to={`/profile/${encodeURIComponent(email)}?from=admin`}
      aria-label={t('gest:admin.governance.viewProfile', { name })}
      className="inline-flex h-8 w-8 touch-manipulation items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
    >
      <Eye size={16} aria-hidden="true" />
    </Link>
  );
}

function CeGrantForm({
  pending,
  errorCode,
  onError,
  onClearError,
}: {
  pending: boolean;
  errorCode: string | null;
  onError: (code: string) => void;
  onClearError: () => void;
}) {
  const { t } = useTranslation();
  const upsert = useUpsertExecutiveGrant();
  const [email, setEmail] = useState('');

  function submitGrant(e: React.FormEvent) {
    e.preventDefault();
    onClearError();
    const trimmed = email.trim();
    if (!trimmed) return;
    upsert.mutate(trimmed, {
      onSuccess: () => setEmail(''),
      onError: (err) => onError(adminExecutiveGrantErrorCode(err)),
    });
  }

  return (
    <>
      <form
        onSubmit={submitGrant}
        className={`${GS_COMPONENTS.card} flex flex-col gap-3 p-4 sm:flex-row sm:items-end`}
      >
        <div className="min-w-0 flex-1">
          <label htmlFor="gov-ce-grant-email" className="text-meta font-medium text-slate-600 dark:text-slate-300">
            {t('gest:admin.executiveGrants.addLabel')}
          </label>
          <input
            id="gov-ce-grant-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('gest:admin.executiveGrants.addPlaceholder')}
            className={`${GS_COMPONENTS.input} mt-1 w-full touch-manipulation`}
            disabled={pending || upsert.isPending}
            autoComplete="off"
          />
        </div>
        <button
          type="submit"
          disabled={pending || upsert.isPending || !email.trim()}
          className={`${GS_COMPONENTS.btnAction} touch-manipulation inline-flex items-center justify-center gap-2`}
        >
          {upsert.isPending ? (
            <Loader2 size={16} className="animate-spin" aria-hidden="true" />
          ) : (
            <Plus size={16} aria-hidden="true" />
          )}
          {t('gest:admin.executiveGrants.add')}
        </button>
      </form>
      {errorCode ? (
        <p className="text-sm text-red-600 dark:text-red-400" role="alert">
          {t(`gest:admin.executiveGrants.errors.${errorCode}`, { defaultValue: errorCode })}
        </p>
      ) : null}
    </>
  );
}

export function GovernanceAccessTab({
  grantsLoading,
  grantsError,
  grantsData,
  membersLoading,
  membersError,
  membersData,
  onRetryGrants,
  onRetryMembers,
}: {
  grantsLoading: boolean;
  grantsError: boolean;
  grantsData: AdminExecutiveGrantsResponse | undefined;
  membersLoading: boolean;
  membersError: boolean;
  membersData: AdminMembersResponse | undefined;
  onRetryGrants: () => void;
  onRetryMembers: () => void;
}) {
  const { t } = useTranslation();
  const revoke = useRevokeExecutiveGrant();
  const [section, setSection] = useState<GovernanceSection>('ce');
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ExecutiveGrantRow | null>(null);

  const sectionOptions: GsSegmentedOption[] = useMemo(
    () => [
      { value: 'ce', label: t('gest:admin.governance.sections.ce') },
      { value: 'cg', label: t('gest:admin.governance.sections.cg') },
      { value: 'directors', label: t('gest:admin.governance.sections.directors') },
    ],
    [t],
  );

  const isLoading = grantsLoading || membersLoading;
  const isError = grantsError || membersError || !grantsData || !membersData;

  const directorEmails = useMemo(
    () => buildDirectorEmailSet(membersData?.teams ?? []),
    [membersData?.teams],
  );

  const ceRows = useMemo(
    () => buildCeGovernanceRows(membersData?.members ?? [], grantsData?.grants ?? [], directorEmails),
    [membersData?.members, grantsData?.grants, directorEmails],
  );

  const cgRows = useMemo(
    () => buildCgGovernanceRows(membersData?.members ?? [], directorEmails),
    [membersData?.members, directorEmails],
  );

  const directorRows = useMemo(
    () => buildDirectorGovernanceRows(membersData?.teams ?? [], membersData?.members ?? [], directorEmails),
    [membersData?.teams, membersData?.members, directorEmails],
  );

  const activeGrantByEmail = useMemo(() => {
    const map = new Map<string, ExecutiveGrantRow>();
    for (const g of grantsData?.grants ?? []) {
      if (g.isActive) map.set(normGovernanceEmail(g.granteeEmail), g);
    }
    return map;
  }, [grantsData?.grants]);

  const pending = revoke.isPending;

  function confirmRevoke() {
    if (!revokeTarget) return;
    setErrorCode(null);
    revoke.mutate(revokeTarget.id, {
      onSuccess: () => setRevokeTarget(null),
      onError: (err) => {
        setErrorCode(adminExecutiveGrantErrorCode(err));
        setRevokeTarget(null);
      },
    });
  }

  const baseColumns: Column<GovernancePersonRow>[] = [
    {
      key: 'name',
      header: t('gest:admin.governance.columns.name'),
      render: (row) => <PersonNameCell row={row} />,
    },
    {
      key: 'email',
      header: t('gest:admin.governance.columns.email'),
      render: (row) => <span className="text-slate-500 dark:text-slate-400">{row.email}</span>,
    },
    {
      key: 'orgLevel',
      header: t('gest:admin.governance.columns.orgLevel'),
      render: (row) =>
        row.orgLevel ? (
          <StatusBadge variant="neutral">{t(orgLevelI18nKey(row.orgLevel))}</StatusBadge>
        ) : (
          <span className="text-slate-400 dark:text-slate-500">{'—'}</span>
        ),
    },
    {
      key: 'status',
      header: t('gest:admin.governance.columns.status'),
      render: (row) => <EmploymentStatusCell row={row} />,
    },
  ];

  const grantDateColumn: Column<GovernancePersonRow> = {
    key: 'grantDate',
    header: t('gest:admin.governance.columns.grantDate'),
    render: (row) =>
      row.grantCreatedAt ? (
        <time dateTime={row.grantCreatedAt} className="text-slate-600 dark:text-slate-300">
          {formatDateTimeFr(row.grantCreatedAt)}
        </time>
      ) : (
        <span className="text-slate-400 dark:text-slate-500">{'—'}</span>
      ),
  };

  const ceColumns: Column<GovernancePersonRow>[] = [
    ...baseColumns.slice(0, 3),
    {
      key: 'access',
      header: t('gest:admin.governance.columns.access'),
      render: (row) => <CeAccessIndicators row={row} />,
    },
    baseColumns[3]!,
    grantDateColumn,
    {
      key: 'actions',
      header: t('gest:admin.governance.columns.actions'),
      className: 'text-right',
      render: (row) => {
        const name = row.fullName ?? row.email;
        const activeGrant = activeGrantByEmail.get(normGovernanceEmail(row.email));
        return (
          <div className="inline-flex items-center gap-1">
            <ViewProfileAction email={row.email} name={name} />
            {activeGrant ? (
              <button
                type="button"
                disabled={pending}
                onClick={() => setRevokeTarget(activeGrant)}
                className={`${GS_COMPONENTS.btnSecondary} touch-manipulation inline-flex items-center gap-1.5 px-2 py-1 text-chip`}
              >
                <Trash2 size={14} aria-hidden="true" />
                {t('gest:admin.executiveGrants.revoke')}
              </button>
            ) : null}
          </div>
        );
      },
    },
  ];

  const cgColumns: Column<GovernancePersonRow>[] = [
    ...baseColumns,
    {
      key: 'actions',
      header: t('gest:admin.governance.columns.actions'),
      className: 'text-right',
      render: (row) => (
        <ViewProfileAction email={row.email} name={row.fullName ?? row.email} />
      ),
    },
  ];

  const directorColumns: Column<GovernancePersonRow>[] = [
    ...baseColumns.slice(0, 3),
    {
      key: 'teams',
      header: t('gest:admin.governance.columns.teams'),
      render: (row) =>
        row.directedTeamNames.length > 0 ? (
          <span className="text-slate-700 dark:text-slate-200">{row.directedTeamNames.join(', ')}</span>
        ) : (
          <span className="text-slate-400 dark:text-slate-500">{'—'}</span>
        ),
    },
    baseColumns[3]!,
    {
      key: 'actions',
      header: t('gest:admin.governance.columns.actions'),
      className: 'text-right',
      render: (row) => (
        <ViewProfileAction email={row.email} name={row.fullName ?? row.email} />
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  if (isError) {
    return (
      <QueryError
        message={t('gest:admin.governance.loadError')}
        onRetry={() => {
          onRetryGrants();
          onRetryMembers();
        }}
      />
    );
  }

  const tableData =
    section === 'ce' ? ceRows : section === 'cg' ? cgRows : directorRows;
  const tableColumns =
    section === 'ce' ? ceColumns : section === 'cg' ? cgColumns : directorColumns;

  return (
    <div className="flex flex-col gap-5">
      <SectionHeader
        title={t('gest:admin.governance.title')}
        description={t('gest:admin.governance.description')}
      />

      <GsSegmented
        ariaLabel={t('gest:admin.governance.sectionNav')}
        options={sectionOptions}
        value={section}
        onChange={(v) => setSection(v as GovernanceSection)}
        className="max-w-xl"
      />

      {section === 'ce' ? (
        <CeGrantForm
          pending={pending}
          errorCode={errorCode}
          onError={setErrorCode}
          onClearError={() => setErrorCode(null)}
        />
      ) : null}

      <DeviationSummary
        rows={section === 'ce' ? ceRows : section === 'cg' ? cgRows : directorRows}
        tab={section}
      />

      {section === 'cg' ? (
        <p className="text-meta text-slate-500 dark:text-slate-400">
          {t('gest:admin.governance.cgHint')}
        </p>
      ) : null}

      {section === 'directors' ? (
        <p className="text-meta text-slate-500 dark:text-slate-400">
          {t('gest:admin.governance.directorsHint')}
        </p>
      ) : null}

      {tableData.length === 0 ? (
        <EmptyState
          icon={<UserCheck size={48} aria-hidden="true" />}
          title={t(`gest:admin.governance.empty.${section}`)}
          description={t(`gest:admin.governance.emptyHint.${section}`)}
        />
      ) : (
        <DataTable
          rowKey={(row) => row.email}
          columns={tableColumns}
          data={tableData}
          emptyMessage={t(`gest:admin.governance.empty.${section}`)}
        />
      )}

      <ConfirmDialog
        open={revokeTarget !== null}
        variant="danger"
        title={t('gest:admin.executiveGrants.revokeConfirmTitle')}
        message={t('gest:admin.executiveGrants.revokeConfirmBody', {
          email: revokeTarget?.granteeEmail ?? '',
        })}
        confirmLabel={t('gest:admin.executiveGrants.revoke')}
        cancelLabel={t('gest:admin.executiveGrants.revokeConfirmCancel')}
        isLoading={revoke.isPending}
        onConfirm={confirmRevoke}
        onCancel={() => setRevokeTarget(null)}
      />
    </div>
  );
}
